import { _isNotEmpty } from '@/littledash';
import type { SendResultReport } from '@/model/Agent.model';
import type { FC } from 'react';
import { truncationText } from '@/components/Agents/SendValidator/utils/SendLimitText';
import styles from './SendResults.module.scss';

interface SendWhatTheReportHoldsProps {
  report: SendResultReport;
  testId?: string;
}

/**
 * The report's exact totals, counted before any limit, beside how much of them it carries, and every
 * limit that left something out. Kept as separate numbers on purpose: "200 findings" could mean 200
 * rules failed or one rule failed on 200 rows, and those call for different responses.
 */
const SendWhatTheReportHolds: FC<SendWhatTheReportHoldsProps> = ({
  report,
  testId = 'invivo-send-validator__report-holds',
}) => {
  const { summary, truncations } = report;
  const numbers: Array<{ key: string; label: string; value: number }> = [
    { key: 'failed_rules', label: 'Rules that found something', value: summary.failed_rules },
    { key: 'finding_groups', label: 'Distinct problems found', value: summary.finding_groups },
    { key: 'affected_records', label: 'Records those problems affect', value: summary.affected_records },
    { key: 'displayed_samples', label: 'Examples shown in this report', value: summary.displayed_samples },
    { key: 'omitted_records', label: 'Affected records no example shows', value: summary.omitted_records },
    { key: 'undecided_checks', label: "Checks that couldn't decide everything", value: summary.undecided_checks },
  ];
  return (
    <div className="ui-card pa4" data-test-component="SendWhatTheReportHolds" data-testid={testId}>
      <h3 className={`${styles.sectionTitle} mb1`}>What this report holds</h3>
      <p className="f6 dark-gray ma0 mb2">
        Counted before any limit, so every total includes what the report does not show.
      </p>
      <ul className={styles.tally} data-test-element="report-numbers">
        {numbers.map(({ key, label, value }) => (
          <li key={key} className={styles.tallyTile} data-test-element="report-number" data-test-key={key}>
            <span className={styles.tallyValue}>{value.toLocaleString('en-US')}</span>
            <span className={styles.tallyLabel}>{label}</span>
          </li>
        ))}
      </ul>
      {_isNotEmpty(truncations) && (
        <div className="mt3" data-test-element="truncations">
          <p className="f6 fw6 near-black ma0 mb1">Limits that left something out</p>
          <ul className="list pa0 ma0">
            {truncations.map((truncation) => (
              <li
                key={`${truncation.limit}-${truncation.scope}`}
                className="f6 dark-gray mb1"
                data-test-element="truncation"
                data-test-key={truncation.limit}
              >
                {truncationText(truncation)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SendWhatTheReportHolds;
